import React, {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import styled, {css} from "styled-components";
import dayjs from "dayjs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faArrowRight} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {NavContext} from "../../contexts/nav.js";
import {SettingsContext} from "../../contexts/settings.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import usePrevious from "../../hooks/usePrevious.js";

// Utils
import {
  BASE_OPTIONS,
  BASE_OPTIONS_PLURAL,
  CUSTOM_OPTION,
  DAY_IDX,
  DAY_NAMES_DAYJS,
  DAY_TO_IDX_DAYJS,
  getNumberSuffix,
  IDX_TO_DAY,
  IDX_TO_DAY_DAYJS
} from "../schedule/helpers.js";
import {
  convertToUserTimezone,
  exists,
  prettyDateInUserTimezone,
  toTitleCase
} from "../../utils/helpers.js";
import {nextStartOnSelectedDays} from "./helpers.js";

// Components
import Modal from "../../components/Modal.js";
import Help from "../../components/Help.js";
import InputText from "../../components/form/InputText.js";
import InputSelect from "../../components/form/InputSelect.js";
import InputCalendar from "../../components/form/InputCalendar.js";
import InputNumber from "../../components/form/InputNumber.js";
import InputError from "../../components/form/InputError.js";
import InputDay from "../../components/form/InputDay.js";

// Style
import {pad} from "../../style/components/variables.js";
import {
  HeadingCenter,
  Button,
  FormField,
  Form,
  FormFieldWrapper,
  Inline,
  Label,
  Input,
  Warning,
  Pill,
  NotLoaded,
  Loader
} from "../../style/components/general.js";

const NO_LAST_DAY_OPTION = ["daily", "weekly", "bi-weekly", "work-week", "semi-monthly"];

const ModalEditChecksheetMeta = ({
  visible,
  setVisible,
  checksheet: {name, lastRecordDateDue, state: definedState, ...otherChecksheetAttr},
  edit,
  setChecksheet,
  isState,
  state
}) => {
  const {api: apiFrequency, data: frequenciesData} = useApi("frequencies");
  const {api: apiFrequency2} = useApi("frequencies");
  const frequencies = Array.isArray(frequenciesData) ? frequenciesData : [];

  const {settings} = useContext(SettingsContext);

  const {allStates} = useContext(NavContext);

  const [loading, setLoading] = useState(true);
  const [resetGuard, setResetGuard] = useState(false);

  const [startPreview, setStartPreview] = useState("");

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return [
      ...Object.entries(allStates).map(([stateName, {id, color}]) => ({
        name: stateName.toUpperCase(),
        id: `${id}`,
        color
      }))
    ];
  }, [allStates]);

  const selectedState = useMemo(() => {
    if (!facilityStates || facilityStates.length === 0 || !state) return undefined;
    if (isState) {
      const stateId = state?.id;
      return facilityStates.find(({id}) => id === `${stateId}`) || null;
    }
    return null;
  }, [facilityStates, isState, state]);

  const schema = yup.object().shape({
    name: yup.string().required("Please provide checksheet name"),
    frequency: yup.string().nullable(),
    recurrence: yup
      .object()
      .nullable()
      .when("frequency", ([freq], s) => {
        if (freq === CUSTOM_OPTION.name)
          return yup.object().shape({
            interval: yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .required("Please provide interval."),
            days: yup
              .array()
              .of(yup.string())
              .when("base", ([base]) => {
                if (base !== "weeks") return yup.mixed().nullable();
                return yup
                  .array()
                  .of(yup.string())
                  .test({
                    test: v => Array.isArray(v) && v.length > 0,
                    message: "Please provide weekday."
                  });
              })
          });

        return s;
      })
  });

  const form = useForm({
    resolver: yupResolver(schema)
  });

  const {
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: {errors}
  } = form;

  const watchFrequency = watch("frequency");
  const watchBase = watch("recurrence.base");
  const watchInterval = watch("recurrence.interval");
  const watchAvailability = watch("recurrence.availability");
  const watchDueDays = watch("recurrence.days");
  const watchLastDay = watch("start.lastDay");
  const watchStartDate = watch("start.date");

  const prevFreq = usePrevious(watchFrequency);
  const prevBase = usePrevious(watchBase);

  const getDefaultDate = useCallback((freq, base) => {
    let date = dayjs();
    let lastDayVal = false;
    if (["weekly", "bi-weekly"].includes(freq) || base === "weeks")
      date = date.endOf("week").add(1, "day");
    if (["monthly", "bi-monthly"].includes(freq) || base === "months") {
      lastDayVal = true;
      date = date.endOf("month");
    }
    if (["annually", "biennially", "triennially"].includes(freq) || base === "years")
      date = date.endOf("year");

    if (freq === "semi-monthly") {
      const midCurrentMonth = Math.floor(date.daysInMonth() / 2);

      if (date.date() > midCurrentMonth) {
        date = date.date(date.daysInMonth());
      } else {
        date = date.date(midCurrentMonth);
      }
    }

    if (freq === "quarterly") {
      const currentMonth = date.month();
      let nextMonth = currentMonth;

      if (nextMonth >= 0 && nextMonth < 3) nextMonth = 2;
      else if (nextMonth >= 3 && nextMonth < 6) nextMonth = 5;
      else if (nextMonth >= 6 && nextMonth < 9) nextMonth = 8;
      else nextMonth = 11;

      date = date.date(1);
      date = date.month(nextMonth);

      const quarterEnd = date.daysInMonth();
      date = date.date(quarterEnd);
    }

    if (freq === "quadrimesterly") {
      const currentMonth = date.month();
      let nextMonth = currentMonth;

      if (nextMonth >= 0 && nextMonth < 4) nextMonth = 3;
      else if (nextMonth >= 4 && nextMonth < 8) nextMonth = 7;
      else nextMonth = 11;

      date = date.date(1);
      date = date.month(nextMonth);

      const quarterEnd = date.daysInMonth();
      date = date.date(quarterEnd);
    }

    if (freq === "semi-annually") {
      const currentMonth = date.month();
      let nextMonth = currentMonth;

      if (nextMonth >= 0 && nextMonth < 6) nextMonth = 5;
      else nextMonth = 11;

      date = date.date(1);
      date = date.month(nextMonth);

      const quarterEnd = date.daysInMonth();
      date = date.date(quarterEnd);
    }

    return {
      date: date.format("YYYY-MM-DD"),
      visibleDate: date.format("MM/DD/YYYY"),
      lastDay: lastDayVal
    };
  }, []);

  const defaultDate = useMemo(
    () => getDefaultDate(watchFrequency, watchAvailability, watchInterval),
    [getDefaultDate, watchAvailability, watchFrequency, watchInterval]
  );

  useEffect(() => {
    if (loading) {
      const now = convertToUserTimezone(dayjs(), settings.timezone);
      const defaults = {
        name: isState ? definedState?.name || name : name,
        start: {
          date: now.format("YYYY-MM-DD"),
          visibleDate: now.format("MM/DD/YYYY"),
          lastDay: true
        },
        recurrence: {
          interval: 1,
          base: "days",
          days: null,
          availability: ""
        },
        frequency: "none"
      };

      const {frequency, availableBase, availableIndex, dateDue, lastDay} = isState
        ? definedState || otherChecksheetAttr
        : otherChecksheetAttr;

      if (dateDue) {
        let date = convertToUserTimezone(dateDue, settings.timezone);

        if (availableBase && availableIndex && frequency) {
          if (frequency.interval === 1 && frequency.base.includes("year"))
            date = date.add(
              12 - availableIndex,
              frequency.base.includes("month") || frequency.interval === 1 ? "month" : "year"
            );
          else
            date = date.add(
              frequency.interval - availableIndex,
              frequency.base.includes("month") || frequency.interval === 1 ? "month" : "year"
            );

          if (lastDay) date = date.date(date.daysInMonth());
        }

        defaults.start.date = date.format("YYYY-MM-DD");

        defaults.start.visibleDate = date.format("MM/DD/YYYY");

        defaults.start.lastDay = lastDay;

        if (frequency?.name === "weekly") {
          const weekdayIdx = dayjs(date).day();
          defaults.recurrence.days = [IDX_TO_DAY_DAYJS[weekdayIdx]];
        }
      } else {
        defaults.start = getDefaultDate(frequency?.name || "none", availableBase, availableIndex);
        if (frequency?.name === "weekly") {
          const weekdayIdx = dayjs(defaults.start.date).day();
          defaults.recurrence.days = [IDX_TO_DAY_DAYJS[weekdayIdx]];
        }
      }

      if (frequency && frequency?.order === null) {
        defaults.frequency = "custom";
        defaults.recurrence.interval = frequency.interval;
        defaults.recurrence.base = frequency.base;
        if (availableBase && availableIndex) defaults.recurrence.availability = `${availableIndex}`;

        defaults.recurrence.days = frequency?.exclude
          ? DAY_IDX.filter(idx => !frequency.exclude.includes(idx)).map(
              idx => IDX_TO_DAY_DAYJS[idx]
            )
          : null;
      } else if (frequency) defaults.frequency = frequency.name;
      else defaults.frequency = "none";

      setLoading(false);
      reset(defaults);
      setResetGuard(true);
    }
  }, [name, settings, otherChecksheetAttr, definedState, isState, getDefaultDate, reset, loading]);

  const showAvailable = useMemo(() => {
    if (loading) return null;
    if (watchFrequency !== CUSTOM_OPTION.name) return false;
    if (watchBase?.includes("month"))
      return watchInterval && watchInterval > 1 && watchInterval < 24;
    if (watchBase?.includes("year")) return watchInterval && watchInterval < 10;
    return false;
  }, [watchBase, watchFrequency, watchInterval, loading]);

  const getNewStartDate = useCallback(
    offset => {
      // if offset is 1, shift to next
      let newStartDate = dayjs(watchStartDate);
      if (watchFrequency === "semi-monthly") {
        let midCurrentMonth = Math.floor(newStartDate.daysInMonth() / 2);
        if (newStartDate.date() === midCurrentMonth) {
          if (offset === 1) newStartDate = newStartDate.date(newStartDate.daysInMonth());
          else newStartDate = newStartDate.date(1).add(-1, "day");
        } else if (offset === 1) newStartDate = newStartDate.add(1, "month").date(midCurrentMonth);
        else {
          newStartDate = newStartDate.add(-1, "month");
          midCurrentMonth = Math.floor(newStartDate.daysInMonth() / 2);
          newStartDate = newStartDate.date(midCurrentMonth);
        }
      } else if (watchFrequency === "quarterly") {
        let nextMonth = newStartDate.month();
        let nextYear = newStartDate.year();
        if (nextMonth === 2) {
          nextMonth = offset === 1 ? 5 : 11;
          if (offset === -1) nextYear -= 1;
        } else if (nextMonth === 5) nextMonth = offset === 1 ? 8 : 2;
        else if (nextMonth === 8) nextMonth = offset === 1 ? 11 : 5;
        else {
          nextMonth = offset === 1 ? 2 : 8;
          if (offset === 1) nextYear += 1;
        }
        newStartDate = newStartDate.year(nextYear).month(nextMonth).date(1);
        const quarterEnd = newStartDate.daysInMonth();
        newStartDate = newStartDate.date(quarterEnd);
      } else if (watchFrequency === "quadrimesterly") {
        let nextMonth = newStartDate.month();
        let nextYear = newStartDate.year();
        if (nextMonth === 3) {
          nextMonth = offset === 1 ? 7 : 11;
          if (offset === -1) nextYear -= 1;
        } else if (nextMonth === 7) nextMonth = offset === 1 ? 11 : 3;
        else {
          nextMonth = offset === 1 ? 3 : 7;
          if (offset === 1) nextYear += 1;
        }
        newStartDate = newStartDate.year(nextYear).month(nextMonth).date(1);
        const quadrimesterEnd = newStartDate.daysInMonth();
        newStartDate = newStartDate.date(quadrimesterEnd);
      } else if (watchFrequency === "semi-annually") {
        let nextMonth = newStartDate.month();
        let nextYear = newStartDate.year();
        if (nextMonth === 5) {
          nextMonth = 11;
          if (offset === -1) nextYear -= 1;
        } else {
          nextMonth = 5;
          if (offset === 1) nextYear += 1;
        }
        newStartDate = newStartDate.year(nextYear).month(nextMonth).date(1);
        const halfYearEnd = newStartDate.daysInMonth();
        newStartDate = newStartDate.date(halfYearEnd);
      }

      return newStartDate;
    },
    [watchFrequency, watchStartDate]
  );

  const shiftStart = useCallback(
    (offset = 1) => {
      const newStartDate = getNewStartDate(offset);

      setValue("start.date", newStartDate.format("YYYY-MM-DD"));
      setValue("start.visibleDate", newStartDate.format("MM/DD/YYYY"));
    },
    [setValue, getNewStartDate]
  );

  useLayoutEffect(() => {
    if (
      (prevFreq !== watchFrequency || !watchBase || watchBase !== prevBase) &&
      !loading &&
      !resetGuard
    ) {
      setValue("recurrence.interval", 1);
      if (prevFreq !== watchFrequency || !watchBase) setValue("recurrence.base", "days");
      if (prevFreq !== watchFrequency || watchBase !== prevBase) {
        setValue("recurrence.days", ["Sun"]);
        setValue("start", defaultDate);
      }
    }

    if (!loading && resetGuard) setResetGuard(false);
  }, [watchFrequency, watchBase, prevFreq, setValue, defaultDate, prevBase, resetGuard, loading]);

  const minDateFromAvailabilityWindow = useMemo(() => {
    if (watchInterval && watchAvailability && watchBase) {
      let due = dayjs();
      if (watchInterval === 1 && watchBase.includes("year"))
        due = due.add(
          12 - watchAvailability,
          watchBase.includes("month") || watchInterval === 1 ? "month" : "year"
        );
      else
        due = due.add(
          watchInterval - watchAvailability,
          watchBase.includes("month") || watchInterval === 1 ? "month" : "year"
        );

      if (watchLastDay) due = due.date(due.daysInMonth());

      return due.format("YYYY-MM-DD");
    }
    return null;
  }, [watchBase, watchInterval, watchLastDay, watchAvailability]);

  const minDateComputed = useMemo(() => {
    if (minDateFromAvailabilityWindow) return minDateFromAvailabilityWindow;
    return dayjs().format("YYYY-MM-DD");
  }, [minDateFromAvailabilityWindow]);

  const minDate = useMemo(() => {
    const minDateComputedParsed = dayjs(minDateComputed);
    if (lastRecordDateDue) {
      const lastCompletedParsed = convertToUserTimezone(lastRecordDateDue, settings?.timezone)?.add(
        1,
        "day"
      );
      if (lastCompletedParsed?.isAfter(minDateComputedParsed, "day"))
        return lastCompletedParsed.format("YYYY-MM-DD");
    }
    return minDateComputed;
  }, [settings, lastRecordDateDue, minDateComputed]);

  // Keep weekday selection aligned with start date (and vice versa) for week-based freqs.
  // Skip setValue when values already match so we don't churn array/date refs and re-enter
  // the form sync loop (especially when minDate would otherwise re-append a deselected day).
  const prevWatchStartDate = usePrevious(watchStartDate);
  useEffect(() => {
    const startDateWeekday = dayjs(watchStartDate).day();
    if (
      (watchBase === "weeks" || watchFrequency === "weekly" || watchFrequency === "bi-weekly") &&
      exists(startDateWeekday) &&
      DAY_NAMES_DAYJS[startDateWeekday] &&
      prevWatchStartDate !== watchStartDate
    ) {
      const targetWeekday = toTitleCase(DAY_NAMES_DAYJS[startDateWeekday]);

      if (
        (!watchDueDays || watchFrequency === "weekly" || watchFrequency === "bi-weekly") &&
        exists(startDateWeekday)
      ) {
        if (!(watchDueDays?.length === 1 && watchDueDays[0] === targetWeekday)) {
          setValue("recurrence.days", [targetWeekday]);
        }
      } else if (targetWeekday && watchDueDays && !watchDueDays.includes(targetWeekday)) {
        setValue(
          "recurrence.days",
          [...watchDueDays, targetWeekday].sort(
            (a, b) => DAY_TO_IDX_DAYJS[a.toLowerCase()] - DAY_TO_IDX_DAYJS[b.toLowerCase()]
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchBase, watchStartDate, setValue]);

  const prevWatchDueDays = usePrevious(watchDueDays);
  useEffect(() => {
    if (
      (watchBase === "weeks" || watchFrequency === "weekly" || watchFrequency === "bi-weekly") &&
      watchDueDays?.length > 0 &&
      prevWatchDueDays !== watchDueDays
    ) {
      const startDateWeekday = dayjs(watchStartDate).day();
      if (watchDueDays.includes(IDX_TO_DAY_DAYJS[startDateWeekday])) return;

      const start = nextStartOnSelectedDays({
        days: watchDueDays,
        fromDate: dayjs(),
        minDate
      });
      if (!start) return;

      if (!start.isSame(dayjs(watchStartDate), "day")) {
        setValue("start.date", start.format("YYYY-MM-DD"));
        setValue("start.visibleDate", start.format("MM/DD/YYYY"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchBase, watchDueDays, setValue, minDate]);

  const frequencyWarning = useMemo(() => {
    if (
      (watchFrequency === "monthly" || watchFrequency === "bi-monthly" || watchBase === "months") &&
      dayjs(watchStartDate).date() > 28
    )
      return `For months with less or greater than 
            ${dayjs(watchStartDate).date()} days, this checksheet will be due on the last day of the
            month`;

    if (watchFrequency === "semi-monthly")
      return "Due date defaults to the middle or last day of the month for semi-monthly tasks.";

    if (watchFrequency === "quarterly")
      return "Due date defaults to the last day of the quarter for quarterly tasks.";

    if (watchFrequency === "quadrimesterly")
      return "Start date defaults to the first day of the year and is due every four months.";

    if (watchFrequency === "semi-annually")
      return "Start date defaults to the first day of the year and is due every six months.";

    if (watchFrequency === CUSTOM_OPTION.name && watchBase?.includes("month"))
      if (watchInterval <= 1)
        return "Availability window not supported for intervals of less than 2 months.";
      else if (watchInterval >= 24)
        return "Availability window not supported for intervals of 24 months or longer.";

    if (watchFrequency === CUSTOM_OPTION.name && watchBase?.includes("year"))
      if (watchInterval > 10)
        return "Availability window not supported for intervals of longer than 10 years.";

    return "";
  }, [watchFrequency, watchBase, watchStartDate, watchInterval]);

  const preventSubmission = useMemo(() => {
    if (watchFrequency === CUSTOM_OPTION.name && watchBase?.includes("month"))
      if (watchInterval < 1 || watchInterval >= 24) return true;

    if (watchFrequency === CUSTOM_OPTION.name && watchBase?.includes("year"))
      if (watchInterval > 10) return true;

    return false;
  }, [watchFrequency, watchBase, watchInterval]);

  // Set preview based on selected frequency
  useEffect(() => {
    if (watchFrequency !== "none" && watchStartDate && watchInterval) {
      const interval = Number.parseInt(watchInterval, 10);

      if (watchFrequency !== "custom" || (!Number.isNaN(interval) && interval > 0)) {
        let params;

        if (watchFrequency !== "custom") params = {start: watchStartDate, name: watchFrequency};

        if (watchFrequency === "custom" && interval && watchBase) {
          params = {
            start: watchStartDate,
            interval,
            base: watchBase,
            exclude:
              watchDueDays && watchDueDays.length > 1 && watchBase === "weeks"
                ? DAY_IDX.filter(idx => !watchDueDays.includes(IDX_TO_DAY[idx]))
                : null
          };
          if (watchAvailability) {
            params.availableBase =
              watchBase.includes("month") || interval === 1 ? "months" : "years";
            params.availableIndex = watchAvailability;
          }
        }
        if (watchLastDay) params.lastDay = watchLastDay;

        if (params)
          apiFrequency2.callGet(null, params).then(result => {
            if (!result || typeof result === "string") return;
            if (result.status === 200 && result.data) setStartPreview(result.data);
          });
      } else {
        setStartPreview("");
      }
    } else {
      setStartPreview("");
    }
  }, [
    apiFrequency2,
    watchStartDate,
    watchLastDay,
    watchFrequency,
    watchInterval,
    watchBase,
    watchDueDays,
    watchAvailability
  ]);

  const hasLastDayOption = useMemo(() => {
    if (!watchFrequency) return false;
    if (watchFrequency === "custom")
      return watchBase?.includes("month") || watchBase?.includes("year");
    return !NO_LAST_DAY_OPTION.includes(watchFrequency);
  }, [watchBase, watchFrequency]);

  const availableOptions = useMemo(() => {
    if (
      watchFrequency !== CUSTOM_OPTION.name ||
      !["month", "months", "year", "years"].includes(watchBase)
    )
      return [];

    const options = [];

    const parsedInterval = parseInt(watchInterval, 10);

    if (exists(parsedInterval) && !Number.isNaN(parsedInterval)) {
      if (watchBase?.includes("month") && parsedInterval > 1) {
        for (let i = 1; i < parsedInterval + 1; i++) {
          options.push({name: i, label: `${i}${getNumberSuffix(i)} Month`});
        }
      }

      if (watchBase?.includes("year")) {
        if (parsedInterval === 1) {
          for (let i = 1; i < 13; i++) {
            options.push({name: i, label: `${i}${getNumberSuffix(i)} Month`});
          }
        } else {
          for (let i = 1; i < parsedInterval + 1; i++) {
            options.push({name: i, label: `${i}${getNumberSuffix(i)} Year`});
          }
        }
      }
    }

    return options;
  }, [watchBase, watchFrequency, watchInterval]);

  const firstDueDate = useMemo(() => {
    if (startPreview && watchAvailability && watchFrequency === CUSTOM_OPTION.name) {
      let due = convertToUserTimezone(startPreview, settings.timezone)
        .add(1, watchBase.includes("month") || watchInterval === 1 ? "month" : "year")
        .subtract(1, "minute");

      if (watchLastDay) due = due.date(due.daysInMonth());

      return due.format("MM/DD/YYYY, h:mm A");
    }
    return null;
  }, [
    startPreview,
    watchBase,
    watchInterval,
    watchFrequency,
    watchLastDay,
    watchAvailability,
    settings
  ]);

  useEffect(() => {
    if (!watchStartDate || !dayjs(watchStartDate).isBefore(dayjs(minDate), "day")) return;

    const isWeekBased =
      watchBase === "weeks" || watchFrequency === "weekly" || watchFrequency === "bi-weekly";

    let clamped = dayjs(minDate);
    if (isWeekBased && watchDueDays?.length > 0) {
      const next = nextStartOnSelectedDays({
        days: watchDueDays,
        fromDate: minDate,
        minDate
      });
      if (!next) return;
      clamped = next;
    }

    if (clamped.isSame(dayjs(watchStartDate), "day")) return;

    setValue("start.date", clamped.format("YYYY-MM-DD"));
    setValue("start.visibleDate", clamped.format("MM/DD/YYYY"));
    setValue("start.lastDay", false);
  }, [watchStartDate, minDate, setValue, watchBase, watchFrequency, watchDueDays]);

  const submitChanges = ({...values}) => {
    if (preventSubmission) return;
    const params = {};

    if (watchFrequency === CUSTOM_OPTION.name) {
      params.interval = Number.parseInt(watchInterval, 10);
      params.base = watchBase;

      if (watchDueDays?.length > 1 && watchBase === "weeks")
        params.exclude = DAY_IDX.filter(idx => !watchDueDays.includes(IDX_TO_DAY[idx]));

      if (showAvailable && params.base && watchAvailability) {
        params.availableBase =
          params.base.includes("month") || params.interval === 1 ? "months" : "years";
        params.availableIndex = parseInt(watchAvailability, 10);
      }
    }

    if (watchFrequency !== "none") {
      params.startDate = watchStartDate;
      params.lastDay = watchLastDay;
    }

    if (watchFrequency !== CUSTOM_OPTION.name) params.frequency = watchFrequency;

    edit(
      "",
      isState ? {state: {...params, name: values.name}} : {...params, name: values.name}
    ).then(({data, status}) => {
      if (data?.data && status === 200) {
        setChecksheet(data.data);
        setVisible(false);
      }
    });
  };

  useEffect(() => {
    apiFrequency.callGet();
  }, [apiFrequency]);

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <HeadingCenter>Edit Checksheet Info</HeadingCenter>

      {loading ? (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      ) : (
        <FormProvider {...form}>
          <Form onSubmit={handleSubmit(submitChanges)} noValidate>
            <FormField>
              <InputText name="name" placeholder="Name" testId="checksheet.editName" label="NAME" />
            </FormField>

            {selectedState !== undefined && selectedState && (
              <>
                <Label bold>STATE</Label>
                <FormField>
                  <Inline>
                    <Pill color={selectedState?.color ? `#${selectedState.color}` : undefined}>
                      {selectedState?.name || "DEFAULT"}
                    </Pill>
                  </Inline>
                </FormField>
              </>
            )}

            <Label bold>
              FREQUENCY&nbsp;
              <Help title="Frequency">
                How often this checksheet is due. Choosing a frequency reveals the dates below,
                showing when the checksheet becomes available and when it is next due.
              </Help>
            </Label>

            {frequencies?.length > 0 && (
              <FormField>
                <InputSelect
                  name="frequency"
                  placeholder="Frequency..."
                  testId="checksheet.frequency"
                  options={[
                    {name: "none", label: "None"},
                    ...frequencies,
                    {name: "custom", label: "Custom..."}
                  ]}
                  required
                />
              </FormField>
            )}

            {watchFrequency === CUSTOM_OPTION.name && (
              <CustomGroup fitcontent>
                <FormField>
                  <Label bold>REPEAT EVERY</Label>
                  <CustomGroup>
                    <InputNumber
                      testId="interval"
                      name="recurrence.interval"
                      min={1}
                      max={10}
                      maxWidth
                      required={false}
                      disableInverse
                      hideError
                    />
                    <BaseSelectWrapper>
                      <InputSelect
                        testId="base"
                        fullWidth
                        name="recurrence.base"
                        options={watchInterval > 1 ? BASE_OPTIONS_PLURAL : BASE_OPTIONS}
                      />
                    </BaseSelectWrapper>
                  </CustomGroup>
                </FormField>
                {showAvailable && (
                  <InputSelect
                    name="recurrence.availability"
                    label="AVAILABLE"
                    options={availableOptions}
                    placeholder="Always"
                  />
                )}
                <InputError errors={errors} name="recurrence.interval" />
              </CustomGroup>
            )}

            {(watchFrequency === "weekly" ||
              watchFrequency === "bi-weekly" ||
              watchBase === "weeks") && (
              <InputDay
                name="recurrence.days"
                restrictOne={watchFrequency !== CUSTOM_OPTION.name}
                restrictWarning={
                  watchFrequency === CUSTOM_OPTION.name
                    ? "Multiple day selection not currently supported for intervals longer than 1 week"
                    : null
                }
              />
            )}

            {watchFrequency && watchFrequency !== "none" && (
              <>
                <Label bold>
                  DATES&nbsp;
                  <Help title="Dates">
                    Available Date is when the checksheet first becomes available to complete.
                    First Due Date is when it is first due. For recurring frequencies, Interval
                    End Date sets the end of the current recurrence and future due dates are
                    calculated from it.
                  </Help>
                </Label>
                <DatesWrapper>
                  <DateWrapper>
                    <Label bold>AVAILABLE DATE</Label>
                    <Input
                      type="text"
                      value={
                        startPreview &&
                        prettyDateInUserTimezone(startPreview, null, "MM/DD/YYYY, h:mm A")
                      }
                      disabled
                    />
                  </DateWrapper>
                  {watchAvailability && (
                    <DateWrapper>
                      <Label bold>FIRST DUE DATE</Label>
                      <Input type="text" value={firstDueDate} disabled />
                    </DateWrapper>
                  )}
                  <DateWrapper>
                    <InputCalendar
                      name="start"
                      label={watchAvailability ? "INTERVAL END DATE" : "FIRST DUE DATE"}
                      showLastDay={hasLastDayOption}
                      min={minDate}
                      disabled={
                        watchFrequency === "semi-monthly" ||
                        watchFrequency === "quarterly" ||
                        watchFrequency === "quadrimesterly" ||
                        watchFrequency === "semi-annually"
                      }
                    />
                  </DateWrapper>
                  {(watchFrequency === "semi-monthly" ||
                    watchFrequency === "quarterly" ||
                    watchFrequency === "quadrimesterly" ||
                    watchFrequency === "semi-annually") && (
                    <ArrowGroup>
                      <ArrowButton
                        onClick={() => shiftStart(-1)}
                        type="button"
                        disabled={getNewStartDate(-1).isBefore(minDate)}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </ArrowButton>
                      <ArrowButton onClick={() => shiftStart(1)} type="button">
                        <FontAwesomeIcon icon={faArrowRight} />
                      </ArrowButton>
                    </ArrowGroup>
                  )}
                </DatesWrapper>
              </>
            )}

            {frequencyWarning && (
              <WarningWrapper>
                <WarningHeader>Warning!</WarningHeader>&nbsp;{frequencyWarning}
              </WarningWrapper>
            )}
            <br />
            <Button type="submit" data-testid="checksheet.saveMeta">
              Save
            </Button>
          </Form>
        </FormProvider>
      )}
    </Modal>
  );
};

ModalEditChecksheetMeta.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired,
  edit: PropTypes.func.isRequired,
  setChecksheet: PropTypes.func.isRequired,
  isState: PropTypes.bool,
  state: PropTypes.objectOf(PropTypes.any)
};

const DateWrapper = styled(FormFieldWrapper)`
  flex: 1 1 0;
  min-width: 0;
  width: auto;

  > div {
    width: 100%;
  }

  > div > div:first-of-type {
    width: 100%;
    max-width: none;
  }

  input {
    width: 100%;
    max-width: none;
  }
`;

const CustomGroup = styled(Inline)`
  margin-top: ${pad / 2}px;
  width: 100%;
  align-items: end;
  justify-content: start;

  div {
    margin: 0;

    ${({fitcontent}) =>
      fitcontent &&
      css`
        width: max-content;
      `}
  }

  input {
    min-width: 35px;
    width: 35px;
  }

  select {
    width: 105px;
  }
`;

const DatesWrapper = styled(Inline)`
  flex-wrap: wrap;
  align-items: flex-end;
  width: 100%;
`;

const ArrowGroup = styled(Inline)`
  align-items: flex-end;
`;

const BaseSelectWrapper = styled.div`
  select {
    max-width: 88px;
  }
`;

const WarningWrapper = styled(Warning)`
  margin-top: ${pad / 2}px;
  color: ${({theme}) => theme.secondary};
`;

const WarningHeader = styled.strong`
  color: ${({theme}) => theme.warning};
`;

const ArrowButton = styled.button`
  background-color: transparent;
  max-width: max-content;

  ${({disabled}) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

export default ModalEditChecksheetMeta;
